// src/pages/taxonomy/Colours/ColoursListPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ColoursAPI } from '../../../api/v3/taxonomy-api-v3';

const BASE = '/app/sales/bagspa/taxonomy';
const includesCi = (h = '', n = '') =>
  (h || '').toString().toLowerCase().includes((n || '').toString().toLowerCase());

export default function ColoursListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // filters & pagination
  const [qName, setQName] = useState('');
  const [per, setPer] = useState(25);
  const [page, setPage] = useState(1);

  async function load() {
    try {
      setLoading(true);
      setError('');
      const payload = await ColoursAPI.list();
      const list =
        payload?.items ??
        payload?.data ??
        (Array.isArray(payload) ? payload : []);
      setRows(list || []);
    } catch (e) {
      setError(e.message || 'Failed to fetch colours');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // filter
  const filtered = useMemo(() => {
    let r = rows;
    if (qName) r = r.filter(x => includesCi(x.name, qName));
    return r;
  }, [rows, qName]);

  // paginate
  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const pageSafe = Math.min(page, totalPages);
  const start = (pageSafe - 1) * per;
  const paged = useMemo(() => filtered.slice(start, start + per), [filtered, start, per]);

  async function handleDelete(id) {
    if (!window.confirm('Delete this item?')) return;
    try {
      await ColoursAPI.destroy(id);
      const nextRows = rows.filter(r => r.id !== id);
      setRows(nextRows);
      const nextFiltered = qName ? nextRows.filter(x => includesCi(x.name, qName)) : nextRows;
      const nextTotalPages = Math.max(1, Math.ceil(nextFiltered.length / per));
      if (pageSafe > nextTotalPages) setPage(nextTotalPages);
    } catch (e) {
      alert(e.message);
    }
  }

  function resetFilters() {
    setQName('');
    setPage(1);
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: 8, flexWrap: 'wrap' }}>
        <h3 className="mb-0">Colours</h3>
        <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            className="form-control"
            style={{ width: 260 }}
            placeholder="Filter by name…"
            value={qName}
            onChange={(e) => { setQName(e.target.value); setPage(1); }}
          />
          <select
            className="form-select"
            style={{ width: 120 }}
            value={per}
            onChange={(e) => { setPer(parseInt(e.target.value, 10)); setPage(1); }}
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}/page</option>)}
          </select>
          <button className="btn btn-outline-secondary" onClick={resetFilters}>Reset</button>
          <Link className="btn btn-primary" to={`${BASE}/colours/new`}>New</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>ID</th>
                  <th>Name</th>
                  <th style={{ width: 200 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.length ? paged.map(row => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.name}</td>
                    <td>
                      <Link className="btn btn-link p-0 me-3" to={`${BASE}/colours/${row.id}/edit`}>
                        Edit
                      </Link>
                      <button
                        className="btn btn-link text-danger p-0"
                        onClick={() => handleDelete(row.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={3} className="text-muted">No data.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">
              Total: {filtered.length} • Page {pageSafe} / {totalPages}
            </div>
            <div className="btn-group">
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe <= 1} onClick={() => setPage(1)}>&laquo;</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>&lsaquo; Prev</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next &rsaquo;</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe >= totalPages} onClick={() => setPage(totalPages)}>&raquo;</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
